// hooks/useGetLocation.js
import { useState, useEffect, useCallback } from 'react';
import { locationService } from '../Services/locationService';
import { useDispatch } from 'react-redux';
import { setSuccessSnack } from '../Reducers/snackSuccess';
import { handleApiError } from '../Utils/ApiErrorHandler';
import { setSnackError } from '../Reducers/snackErrors';


const useGetLocations = (role) => {
  const dispatch = useDispatch();
  const [locations, setList] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [locationContacts, setLocationContacts] = useState([]);
  const [locationPrograms, setLocationPrograms] = useState([]);
  const [locationSubjects, setLocationSubjects] = useState([]);

  
  // Separate loading states
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);



  // GET operations
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await locationService.getLocations();
      setList(data);
      return data;
    } catch (error) {
      handleApiError(error, dispatch, 'fetch locations');
      return null;
    } finally {
      setLoading(false);
    }
  }, [dispatch, role]);

  const fetchSemesterLocation = async (location_id) => {
    if (!location_id) return null;
    
    try {
      setLoading(true);
      const data = await locationService.getSemesterLocation(location_id);
      setSemesters(data);
      return data;
    } catch (error) {
      return handleApiError(error, dispatch, 'fetch semesters');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationContacts = async (id) => {
    if (!id) return null;
    
    try {
      setLoading(true);
      const data = await locationService.getLocationContacts(id);
      setLocationContacts(data);
      return data;
    } catch (error) {
      return handleApiError(error, dispatch, 'fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationPrograms = async (location_id) => {
    if (!location_id) return null;
    
    try {
      setLoading(true);
      const data = await locationService.getLocationPrograms(location_id);
      setLocationPrograms(data);
      return data;
    } catch (error) {
      return handleApiError(error, dispatch, 'fetch programs');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationTutors = async (tutor_id) => {
    try {
      setLoading(true);
      const data = await locationService.getLocationTutors(tutor_id);
      setList(data);
      return data;
    } catch (error) {
      return handleApiError(error, dispatch, 'fetch locations by tutors');
    } finally {
      setLoading(false);
    }
  };

  // POST/CREATE operations
  const createLocation = async (payload) => {
    try {
      setCreating(true);
      const data = await locationService.createLocation(payload);
      
      if (data.status === 'OK') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: `Created location ${data.id}`
        }));
        await fetchData(); // Refresh list
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'create location');
      return false;
    } finally {
      setCreating(false);
    }
  };

  const createLocationContact = async (payload) => {
    try {
      setCreating(true);
      const data = await locationService.createLocationContact(payload);
      
      if (data.status === 'OK') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: `Created contact ${data.id}`
        }));
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'create contact');
      return false;
    } finally {
      setCreating(false);
    }
  };

  // UPDATE operations
  const updateLocation = async (payload) => {
    try {
      setUpdating(true);
      const data = await locationService.updateLocation(payload);
      
      if (data.status === 'Updated') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: `Updated location ${data.id}`
        }));
        await fetchData(); // Refresh list
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'update location');
      return false;
    } finally {
      setUpdating(false);
    }
  };

  // DELETE operations
  const deleteLocation = async (id) => {
    if (!id) return false;
    
    try {
      setDeleting(true);
      const data = await locationService.deleteLocation(id);
      
      if (data.status === "Removed") {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: 'Removed location'
        }));
        await fetchData(); // Refresh list
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'delete location');
      return false;
    } finally {
      setDeleting(false);
    }
  };

  const createProgramLocation = async (values) => {
    // Validate required fields
    if (!values.location_id || !values.program_id) { return ;}
  
    try {
      setCreating(true);
      const data = await locationService.createProgramLocation(values);
      if (data.status === 'OK') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: 'Created new program to location.'
        }));
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'create program location');
      return false;
    } finally {
      setCreating(false);
    }
  };

  const deleteProgramLocation = async (program_id, location_id) => {
    if (!program_id || !location_id) return false;
  
    try {
      setDeleting(true);
      const data = await locationService.deleteProgramLocation(program_id, location_id);
      if (data.status === 'OK') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: 'Removed program'
        }));
        await fetchLocationPrograms(location_id); // Refresh programs list
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'delete program location');
      return false;
    } finally {
      setDeleting(false);
    }
  };

  const createSemesterLocation = async (values) => {
    if (!values.location_id || !values.semester_id || !values.organization_id) {
      dispatch(setSnackError({
        title: 'Error', 
        body: 'Missing required values.'
      }));
      return false;
    }
  
    try {
      setCreating(true);
      const data = await locationService.createSemesterLocation(values);
      if (data.status === 'OK') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: 'Created new semester location.'
        }));
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'create semester location');
      return false;
    } finally {
      setCreating(false);
    }
  };
  const deleteSemesterLocation = async (id) => {
    if (!id) return false;
  
    try {
      setDeleting(true);
      const data = await locationService.deleteSemesterLocation(id);
      if (data.status === 'OK') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: 'Removed semester location'
        }));
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'delete semester location');
      return false;
    } finally {
      setDeleting(false);
    }
  };

  const createSubjectLocation = async (values) => {

    if (!values.location_id) {
      dispatch(setSnackError({
        title: 'Error', 
        body: 'Missing required values.'
      }));
      return false;
    }
  
    try {
      setCreating(true);
      const data = await locationService.createSubjectLocation(values);
      if (data.status === 'OK') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: 'Created new subject to location.'
        }));
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'create subject location');
      return false;
    } finally {
      setCreating(false);
    }
  };

  const deleteSubjectLocation = async (subject_id, location_id) => {
    if (!subject_id || !location_id) return false;
  
    try {
      setDeleting(true);
      const data = await locationService.deleteSubjectLocation(subject_id, location_id);
      if (data.status === 'Removed') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: 'Removed subject'
        }));
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'delete subject location');
      return false;
    } finally {
      setDeleting(false);
    }
  };

  const fetchLocationSubjects = async (location_id) => {
    if (!location_id) return null;
    try {
      setLoading(true);
      const data = await locationService.getLocationSubjects(location_id);
      setLocationSubjects(data);
      return data;
    } catch (error) {
      return handleApiError(error, dispatch, 'fetch location subjects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { 
    // Data
    locations, 
    semesters,
    locationContacts, 
    locationPrograms,
    locationSubjects,
    
    // Loading states
    loading,
    creating,
    updating,
    deleting,
    
    refetch: fetchData, 
    fetchSemesterLocation, 
    fetchLocationContacts, 
    fetchLocationPrograms,
    fetchLocationSubjects,
    fetchLocationTutors,

    createLocation,
    createLocationContact,
    updateLocation,
    deleteLocation,
    createProgramLocation,
    deleteProgramLocation,
    createSemesterLocation,
    deleteSemesterLocation,
    createSubjectLocation,
    deleteSubjectLocation,
  };
};

export default useGetLocations;